import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatTime } from '../../utils/formatTime';
import { humanizeSource } from '../../utils/humanizeSource';
import { Panel, Badge, Button, ChipGroup } from '../../components/design';

const CORE_API = '/api';

export default function AlertsTab({ serverId, canEdit }) {
  const queryClient = useQueryClient();
  const [alertSeverityFilter, setAlertSeverityFilter] = useState('');
  const [alertStatusFilter, setAlertStatusFilter] = useState('');
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [alertActionLoading, setAlertActionLoading] = useState(null);

  const { data: alertsData, isLoading: alertsLoading, error: alertsError, refetch: refetchAlerts } = useQuery({
    queryKey: ['alerts', 'server', serverId],
    queryFn: () => fetch(`${CORE_API}/incidents?server_id=${serverId}&limit=50`).then(res => res.json()),
    refetchInterval: 10000,
  });

  const { data: mitreData } = useQuery({
    queryKey: ['servers', serverId, 'mitre'],
    queryFn: () => fetch(`${CORE_API}/servers/${serverId}/mitre`).then(r => r.ok ? r.json() : { techniques: [] }),
    enabled: !!serverId,
    refetchInterval: 60000,
    retry: 0,
  });
  const mitreTechniques = mitreData?.techniques || [];

  const alerts = alertsData?.incidents || [];

  const filteredAlerts = alerts.filter(a => {
    if (alertSeverityFilter && a.severity !== alertSeverityFilter) return false;
    if (alertStatusFilter && a.status !== alertStatusFilter) return false;
    return true;
  });

  const handleAcknowledge = async (incidentId) => {
    setAlertActionLoading(incidentId);
    try {
      const res = await fetch(`${CORE_API}/incidents/${incidentId}/acknowledge`, { method: 'POST' });
      if (res.ok) queryClient.invalidateQueries({ queryKey: ['alerts', 'server', serverId] });
    } catch {}
    setAlertActionLoading(null);
  };

  const handleResolve = async (incidentId) => {
    setAlertActionLoading(incidentId);
    try {
      const res = await fetch(`${CORE_API}/incidents/${incidentId}/resolve`, { method: 'POST' });
      if (res.ok) queryClient.invalidateQueries({ queryKey: ['alerts', 'server', serverId] });
    } catch {}
    setAlertActionLoading(null);
  };

  const maxMitre = Math.max(1, ...mitreTechniques.map(t => t.count || 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel title="MITRE Techniques" sub={`${mitreTechniques.length} detected · 30d`} noPad>
        {mitreTechniques.length === 0 ? (
          <div className="empty">No MITRE techniques detected</div>
        ) : (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mitreTechniques.map(t => (
              <div key={t.technique} className="list-row" style={{ padding: '6px 0' }}>
                <span className="mono" style={{ flex: '0 0 90px', color: 'var(--text-1)', fontWeight: 700 }}>{t.technique}</span>
                <div className="progress" style={{ flex: 1 }}>
                  <span style={{ width: `${Math.round(((t.count || 0) / maxMitre) * 100)}%`, background: 'var(--grad-main)' }} />
                </div>
                <b className="mono" style={{ fontSize: 12 }}>{t.count}</b>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title={alerts.length > 0 ? `Incidents (${filteredAlerts.length}/${alerts.length})` : 'Incidents'} noPad
        actions={<Button variant="ghost" size="sm" icon="refresh-cw" onClick={() => refetchAlerts()} disabled={alertsLoading}>Refresh</Button>}>
      <div className="filter-bar" style={{ margin: '16px 16px 0' }}>
        <ChipGroup
          value={alertSeverityFilter}
          onChange={setAlertSeverityFilter}
          options={[
            { value: '', label: 'All severities' },
            { value: 'critical', label: 'Critical' },
            { value: 'high', label: 'High' },
            { value: 'warning', label: 'Warning' },
            { value: 'info', label: 'Info' },
          ]}
        />
        <select value={alertStatusFilter} onChange={e => setAlertStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {alertsLoading && filteredAlerts.length === 0 ? (
        <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>Loading incidents...</div>
      ) : alertsError ? (
        <div className="empty">
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>Failed to load: {alertsError.message}</h3>
          <Button variant="danger" size="sm" onClick={() => refetchAlerts()}>Retry</Button>
        </div>
      ) : filteredAlerts.length === 0 ? (
        <div className="empty">
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>{alertSeverityFilter || alertStatusFilter ? 'No incidents match the selected filters' : 'No incidents recorded for this server'}</h3>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredAlerts.map(inc => (
              <div key={inc.id}
                style={{
                  borderRadius: 12, background: 'var(--glass)',
                  border: '1px solid var(--glass-border)',
                  borderLeft: `4px solid ${inc.severity === 'critical' ? 'var(--danger)' : inc.severity === 'high' ? '#f97316' : inc.severity === 'warning' ? 'var(--warning)' : 'var(--info)'}`,
                }}>
                <div style={{ padding: 12 }}>
                  <div className="flex items-start justify-between gap-2">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="flex items-center gap-2">
                        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inc.title}</span>
                        {inc.category && (
                          <Badge tone="pink" dot={false}>{inc.category}</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge tone={inc.severity === 'critical' ? 'red' : inc.severity === 'high' ? 'amber' : inc.severity === 'warning' ? 'amber' : 'blue'}>{inc.severity}</Badge>
                      <Badge tone={inc.status === 'open' ? 'red' : inc.status === 'acknowledged' ? 'amber' : 'green'}>{inc.status}</Badge>
                    </div>
                  </div>

                  <div
                    onClick={() => setExpandedAlert(expandedAlert === inc.id ? null : inc.id)}
                    style={{ cursor: 'pointer', marginTop: 4 }}>
                    <p className="muted" style={{ fontSize: 12 }}>
                      {expandedAlert === inc.id ? (inc.description || 'No description') : (inc.description ? inc.description.substring(0, 120) + (inc.description.length > 120 ? '...' : '') : 'No description')}
                    </p>
                  </div>

                  {expandedAlert === inc.id && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {inc.alert_rule && (
                        <div className="flex items-center gap-2" style={{ fontSize: 11 }}>
                          <span className="muted" style={{ fontWeight: 500 }}>Alert rule:</span>
                          <span className="mono" style={{ color: 'var(--accent-1)' }}>{inc.alert_rule}</span>
                        </div>
                      )}
                      {inc.detected_by && (
                        <div className="flex items-center gap-2" style={{ fontSize: 11 }}>
                          <span className="muted" style={{ fontWeight: 500 }}>Detected by:</span> {humanizeSource(inc.detected_by)}
                        </div>
                      )}
                      {inc.service && (
                        <div className="flex items-center gap-2" style={{ fontSize: 11 }}>
                          <span className="muted" style={{ fontWeight: 500 }}>Service:</span> {humanizeSource(inc.service)}
                        </div>
                      )}
                      {inc.parent_incident_id && (
                        <div className="flex items-center gap-2" style={{ fontSize: 11 }}>
                          <span style={{ fontWeight: 500, color: 'var(--accent-1)' }}>Parent:</span>
                          <a href={`#incidents/${inc.parent_incident_id}`} style={{ color: 'var(--accent-1)' }}>
                            {inc.parent_title || inc.parent_incident_id.substring(0,8) + '...'}
                          </a>
                        </div>
                      )}
                      {inc.related_incidents && inc.related_incidents.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span className="muted" style={{ fontWeight: 500, fontSize: 11 }}>Related:</span>
                          {inc.related_incidents.slice(0, 5).map(ri => (
                            <a key={ri.id} href={`#incidents/${ri.id}`}
                              className="flex items-center gap-2" style={{ fontSize: 11, color: 'var(--accent-1)', marginLeft: 16 }}>
                              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 9999, background: ri.severity === 'critical' ? 'var(--danger)' : ri.severity === 'high' ? '#ec4899' : '#a78bfa' }} />
                              {ri.title}
                            </a>
                          ))}
                          {inc.related_incidents.length > 5 && (
                            <span className="muted" style={{ fontSize: 10, marginLeft: 16 }}>+{inc.related_incidents.length - 5} more</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2" style={{ fontSize: 11 }}>
                        <span className="muted" style={{ fontWeight: 500 }}>Created:</span> {formatTime(inc.created_at)}
                        {inc.resolved_at && (
                          <><span className="muted">|</span><span className="muted" style={{ fontWeight: 500 }}>Resolved:</span> {formatTime(inc.resolved_at)}</>
                        )}
                      </div>

                      <div className="flex gap-2" style={{ paddingTop: 4 }}>
                        {canEdit && inc.status === 'open' && (
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleAcknowledge(inc.id); }}
                            disabled={alertActionLoading === inc.id}>
                            {alertActionLoading === inc.id ? '...' : 'Acknowledge'}
                          </Button>
                        )}
                        {canEdit && inc.status !== 'resolved' && (
                          <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); handleResolve(inc.id); }}
                            disabled={alertActionLoading === inc.id}>
                            {alertActionLoading === inc.id ? '...' : 'Resolve'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between" style={{ marginTop: 6 }}>
                    <span className="muted" style={{ fontSize: 10 }}>
                      {formatTime(inc.created_at)}
                    </span>
                    <button onClick={() => setExpandedAlert(expandedAlert === inc.id ? null : inc.id)}
                      style={{ fontSize: 10, color: 'var(--accent-1)', background: 'none', border: 'none', cursor: 'pointer' }}>
                      {expandedAlert === inc.id ? 'Collapse' : 'Details'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </Panel>
    </div>
  );
}
